import { useEffect, useState, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useSearchParams } from 'react-router-dom';
import { useAppNavigate, usePwaNav } from '../contexts/PwaNavContext';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { motion, AnimatePresence } from 'motion/react';
import { io, type Socket } from 'socket.io-client';
import { ScanLine, ArrowRight, BookOpen, ChevronRight, ChevronDown, QrCode, Users, History, ClipboardList, ChefHat, CheckCircle, Bell, PlusCircle, Gift, CalendarCheck, Frown } from 'lucide-react';
import { useCustomerAuth } from '../contexts/CustomerAuthContext';
import FirebaseAuthModal from '../components/FirebaseAuthModal';
import QrScannerModal from '../components/QrScannerModal';
import CustomerUserMenu from '../components/CustomerUserMenu';
import { NearbyStrip } from '../components/home/NearbyStrip';
import { VenueStrip } from '../components/home/VenueStrip';
import { API_URL } from '../utils/api';
import { setStoredLang, type LangCode } from '../i18n';
import FriendInviteModal from '../components/FriendInviteModal';
import LegalFooter from '../components/LegalFooter';
import './HomePage.css';

interface ActiveOrder {
  id: string;
  status: string;
  restaurantName: string;
  qrCode: string;
  parentOrderId?: string | null;
  totalAmount?: number;
}

interface ActiveFriend {
  friend: { id: string; name: string };
  order: { id: string; restaurantName: string; status: string };
  items: { name: string; quantity: number }[];
}

const containerVariants = {
  hidden: {},
  visible: {
    transition: { staggerChildren: 0.07 },
  },
};

const cardVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.35, ease: 'easeOut' as const } },
};

export default function HomePage() {
  const { t, i18n } = useTranslation();
  const navigate = useAppNavigate();
  const { isAuthenticated, customer, token, loading: authLoading } = useCustomerAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  const [showAuth, setShowAuth] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const langWrapRef = useRef<HTMLDivElement>(null);
  const [activeOrders, setActiveOrders] = useState<ActiveOrder[]>([]);
  // Starts true: until the first lookup resolves we don't know whether there is
  // an active order, and rendering the empty state in the meantime makes a
  // returning customer think their order vanished.
  const [loadingOrder, setLoadingOrder] = useState(true);
  const [activeFriends, setActiveFriends] = useState<ActiveFriend[]>([]);
  const [showScanner, setShowScanner] = useState(false);
  const fetchActiveFriendsRef = useRef<(() => void) | null>(null);


  // ── Debug panel (temporary — set to true to re-enable) ──
  const DEBUG_PANEL = false;
  const [debugLines, setDebugLines] = useState<string[]>([]);
  const [debugOpen, setDebugOpen] = useState(true);
  const addDebug = useCallback((msg: string) => {
    if (!DEBUG_PANEL) return;
    const ts = new Date().toISOString().slice(11, 19);
    setDebugLines(prev => [...prev.slice(-30), `${ts} ${msg}`]);
  }, []);
  useEffect(() => {
    const check = () => {
      const log = (window as unknown as Record<string, string[]>).__authLog;
      if (log?.length) {
        setDebugLines(prev => [...prev, ...log]);
        (window as unknown as Record<string, string[]>).__authLog = [];
      }
    };
    check();
    const timer = setInterval(check, 1000);
    return () => clearInterval(timer);
  }, []);
  useEffect(() => {
    const standalone = window.matchMedia('(display-mode: standalone)').matches;
    const url = window.location.href;
    const hasCt = new URL(url).searchParams.has('_ct');
    const hasLsToken = !!localStorage.getItem('olso_customer_token');
    const hasLsCust = !!localStorage.getItem('olso_customer_data');
    const cookies = document.cookie;
    const hasJwtCookie = cookies.includes('olso_jwt=');
    addDebug(`PAGE=HomePage mode=${standalone ? 'PWA' : 'browser'}`);
    addDebug(`url_ct=${hasCt} ls_token=${hasLsToken} ls_cust=${hasLsCust}`);
    addDebug(`cookie_jwt=${hasJwtCookie} isAuth=${isAuthenticated}`);
    addDebug(`url=${url.slice(0, 100)}`);
    // Check manifest link
    const manifestLink = document.querySelector('link[rel="manifest"]') as HTMLLinkElement | null;
    addDebug(`manifest=${manifestLink ? manifestLink.href.slice(0, 60) : 'NONE'}`);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { showOrder, showFeed, showRestaurant, showVenue, showPassport, showHistory, showGift, showFriendProfile, showSettings, showLogin, showFriends } = usePwaNav();
  const prevShowOrder = useRef(false);
  const anyOverlayOpen = showOrder || showFeed || showRestaurant || showVenue || showPassport || showHistory || showGift || showFriendProfile || showSettings || showLogin || showFriends;
  const prevAnyOverlayRef = useRef(anyOverlayOpen);

  const fetchActiveOrder = useCallback(async () => {
    if (!token) return;
    setLoadingOrder(true);
    try {
      const base = API_URL || '';
      const { data } = await axios.get(`${base}/api/orders/customer/active-orders`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      console.log('[home] active-orders response:', Array.isArray(data) ? data.length : 'not-array', data);
      setActiveOrders(Array.isArray(data) ? data : []);
    } catch (err) {
      console.warn('[home] active-orders fetch failed:', err);
      setActiveOrders([]);
    } finally {
      setLoadingOrder(false);
    }
  }, [token]);

  useEffect(() => {
    if (!isAuthenticated || !token) return;
    fetchActiveOrder();
  }, [isAuthenticated, token, fetchActiveOrder]);

  // Guests have no account to query, so their in-flight order lives only in
  // localStorage (written by useOrderSocket). Restore it here, otherwise
  // leaving the order screen strands them: the QR is already marked scanned,
  // so it is gone from the venue display and cannot be scanned again.
  useEffect(() => {
    if (authLoading) return; // don't decide until we know who this is
    // Mirror the authenticated effect's condition exactly, so between the two
    // of them the loading state is always resolved — a half-authenticated state
    // (token present, session not established) would otherwise hang the skeleton.
    if (isAuthenticated && token) return;

    let qr: string | null = null;
    try {
      qr = localStorage.getItem('olso_active_order');
      if (qr && localStorage.getItem(`olso_order_done_${qr}`)) qr = null;
    } catch {
      setLoadingOrder(false);
      return; // storage unavailable (private mode) — nothing to restore
    }
    if (!qr) { setLoadingOrder(false); return; }

    let cancelled = false;
    (async () => {
      try {
        const base = API_URL || '';
        const { data } = await axios.get(`${base}/api/orders/qr/${qr}`);
        if (cancelled || !data?.id) return;
        if (data.status === 'COMPLETED' || data.status === 'CANCELLED') {
          try {
            localStorage.removeItem('olso_active_order');
            localStorage.setItem(`olso_order_done_${qr}`, '1');
          } catch { /* ignore */ }
          return;
        }
        setActiveOrders([{
          id: data.id,
          status: data.status,
          restaurantName: data.restaurant?.name ?? '',
          qrCode: data.qrCode ?? qr!,
          totalAmount: data.totalAmount,
        }]);
      } catch {
        // Order unreachable or gone — leave home as-is rather than erroring.
      } finally {
        if (!cancelled) setLoadingOrder(false);
      }
    })();
    return () => { cancelled = true; };
  }, [authLoading, isAuthenticated, token]);

  useEffect(() => {
    if (!isAuthenticated || !token) return;
    const onVisibility = () => {
      if (!document.hidden) fetchActiveOrder();
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, [isAuthenticated, token, fetchActiveOrder]);

  // Aggressive polling: refetch active order every 10s while home is visible.
  // Belt-and-suspenders against stale state when sockets miss / overlays mis-trigger.
  useEffect(() => {
    if (!isAuthenticated || !token) return;
    const id = window.setInterval(() => {
      if (!document.hidden && !anyOverlayOpen) fetchActiveOrder();
    }, 10000);
    return () => window.clearInterval(id);
  }, [isAuthenticated, token, anyOverlayOpen, fetchActiveOrder]);

  // Refetch when user navigates back to home from any overlay within PWA
  useEffect(() => {
    if (prevAnyOverlayRef.current && !anyOverlayOpen && isAuthenticated && token) {
      fetchActiveOrder();
    }
    prevAnyOverlayRef.current = anyOverlayOpen;
    prevShowOrder.current = showOrder;
  }, [anyOverlayOpen, showOrder, isAuthenticated, token, fetchActiveOrder]);

  useEffect(() => {
    if (!isAuthenticated || !token) return;
    const fetchActive = async () => {
      try {
        const base = API_URL || '';
        const { data } = await axios.get(`${base}/api/gifts/active-friends`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setActiveFriends(data);
      } catch { /* ignore */ }
    };
    fetchActiveFriendsRef.current = fetchActive;
    fetchActive();
    const interval = setInterval(fetchActive, 60000);
    const onFocus = () => fetchActive();
    const onVisibility = () => { if (document.visibilityState === 'visible') fetchActive(); };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      fetchActiveFriendsRef.current = null;
      clearInterval(interval);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [isAuthenticated, token]);

  // Socket: join customer room + each active order room to receive real-time updates
  const socketRef = useRef<Socket | null>(null);
  const joinedOrderRoomsRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    if (!isAuthenticated || !token || !customer?.id) return;
    const socketUrl = API_URL ||
      (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
        ? 'http://localhost:3001'
        : `${window.location.protocol}//${window.location.hostname}:3001`);
    const socket: Socket = io(socketUrl, { transports: ['websocket', 'polling'] });
    socketRef.current = socket;
    socket.on('connect', () => {
      socket.emit('join-customer-room', { customerId: customer.id, token });
      // Re-join any order rooms we were tracking before reconnect
      joinedOrderRoomsRef.current.forEach(orderId => socket.emit('join-order', orderId));
    });
    socket.on('friendship-accepted', () => {
      fetchActiveFriendsRef.current?.();
    });
    socket.on('order-updated', (o: { id: string; status: string; qrCode?: string }) => {
      if (!o?.id) return;
      if (o.status === 'COMPLETED') {
        // Mark done so cold PWA launch doesn't reopen this order
        const savedQr = localStorage.getItem('olso_active_order');
        if (o.qrCode) {
          localStorage.setItem(`olso_order_done_${o.qrCode}`, '1');
          if (savedQr === o.qrCode) localStorage.removeItem('olso_active_order');
        }
        setActiveOrders(prev => prev.filter(ao => ao.id !== o.id));
        joinedOrderRoomsRef.current.delete(o.id);
      } else if (o.status === 'CANCELLED') {
        // Keep CANCELLED on home with refund banner — user dismisses with "Понятно".
        // Stop polling status (no more transitions); cleanup PWA cold-launch hint.
        const savedQr = localStorage.getItem('olso_active_order');
        if (o.qrCode) {
          localStorage.setItem(`olso_order_done_${o.qrCode}`, '1');
          if (savedQr === o.qrCode) localStorage.removeItem('olso_active_order');
        }
        setActiveOrders(prev => prev.map(ao => ao.id === o.id ? { ...ao, status: 'CANCELLED' } : ao));
      } else {
        // Update status in-place (PENDING → PREPARING → READY)
        setActiveOrders(prev => prev.map(ao => ao.id === o.id ? { ...ao, status: o.status } : ao));
      }
    });
    return () => {
      socketRef.current = null;
      joinedOrderRoomsRef.current.clear();
      socket.disconnect();
    };
  }, [isAuthenticated, token, customer?.id]);

  // Join/leave order rooms as activeOrders list changes
  useEffect(() => {
    const socket = socketRef.current;
    if (!socket) return;
    const currentIds = new Set(activeOrders.map(o => o.id));
    // Join new rooms
    currentIds.forEach(id => {
      if (!joinedOrderRoomsRef.current.has(id)) {
        joinedOrderRoomsRef.current.add(id);
        socket.emit('join-order', id);
      }
    });
  }, [activeOrders]);

  // Handle ?ref=CODE for already-authenticated users — show invite modal
  const [refToast, setRefToast] = useState<{ msg: string; type: 'success' | 'error' } | null>(null);
  const [pendingRefCode, setPendingRefCode] = useState<string | null>(null);
  useEffect(() => {
    const refCode = searchParams.get('ref');
    if (!refCode) return;
    // Show preview modal for anon and authenticated users alike.
    // For anon — keep ?ref in URL until they log in (so refresh / accidental close doesn't lose it).
    setPendingRefCode(refCode);
    if (isAuthenticated && token) {
      const next = new URLSearchParams(searchParams);
      next.delete('ref');
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, isAuthenticated, token, setSearchParams]);

  const handleScanQR = () => setShowScanner(true);

  const handleScanResult = (code: string) => {
    setShowScanner(false);
    navigate(`/order/${code}`);
  };

  const getStatusClass = (status: string) => {
    if (status === 'PENDING') return 'queue';
    if (status === 'PREPARING') return 'cooking';
    if (status === 'READY') return 'ready';
    return 'done';
  };

  const getStatusLabel = (status: string) => {
    const map: Record<string, string> = {
      PENDING:   t('kitchen.status.pending'),
      PREPARING: t('kitchen.status.preparing'),
      READY:     t('kitchen.status.ready'),
      COMPLETED: t('kitchen.status.completed'),
    };
    return map[status] || status;
  };

  // Close lang dropdown on outside click / Escape
  useEffect(() => {
    if (!langOpen) return;
    const onDoc = (e: MouseEvent) => {
      if (langWrapRef.current && !langWrapRef.current.contains(e.target as Node)) {
        setLangOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setLangOpen(false); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [langOpen]);

  const firstName = customer?.name?.split(' ')[0] || '';
  const langLabel: Record<LangCode, string> = { ru: 'РУ', en: 'EN', kk: 'ҚАЗ' };

  return (
    <div className="hp-container">

      {/* ── REFERRAL TOAST ── */}
      {refToast && (
        <div style={{
          position: 'fixed', top: 16, left: 16, right: 16, zIndex: 100,
          padding: '12px 16px', borderRadius: 12,
          background: refToast.type === 'success' ? 'var(--a-green-deep-success)' : 'var(--a-red-deep)',
          color: 'var(--a-white)', fontSize: 14, fontWeight: 600, textAlign: 'center',
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
        }}>
          {refToast.msg}
        </div>
      )}

      {/* ── HEADER ── */}
      <div className="hp-header">
        <img src="/logo-olso.png" alt="OLSO" className="hp-logo" />
        <div className="hp-header-right">
          <div className="hp-lang-dropdown" ref={langWrapRef}>
            <button
              type="button"
              className="hp-lang-toggle"
              aria-haspopup="listbox"
              aria-expanded={langOpen}
              onClick={() => setLangOpen(o => !o)}
            >
              {langLabel[i18n.language as LangCode] ?? 'РУ'}
              <ChevronDown size={14} className={`hp-lang-chev ${langOpen ? 'is-open' : ''}`} />
            </button>
            {langOpen && (
              <ul className="hp-lang-menu" role="listbox">
                {(['ru', 'en', 'kk'] as LangCode[]).map(lang => (
                  <li key={lang}>
                    <button
                      type="button"
                      className={`hp-lang-option ${i18n.language === lang ? 'is-active' : ''}`}
                      role="option"
                      aria-selected={i18n.language === lang}
                      onClick={() => { setStoredLang(lang); setLangOpen(false); }}
                    >
                      {langLabel[lang]}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
          {isAuthenticated ? (
            <CustomerUserMenu />
          ) : (
            <button className="hp-login-btn" onClick={() => setShowAuth(true)}>
              {t('common.login')}
            </button>
          )}
        </div>
      </div>

      {/* ── GUEST STATE ── */}
      {!isAuthenticated && (
        <div className="hp-guest">

          <div className="hp-guest-hero">
            <div className="hp-eyebrow">
              <div className="hp-eyebrow-dot" />
              {t('home.eyebrow')}
            </div>
            <h1 className="hp-title">
              {t('home.title1')}<br />
              <em>{t('home.title2')}</em>
            </h1>
            <p className="hp-sub">{t('home.sub')}</p>
          </div>

          <div className="hp-scan-cta-wrap">
            <button type="button" className="hp-scan-cta" onClick={handleScanQR}>
              <span className="hp-scan-cta-icon"><ScanLine size={28} strokeWidth={2.2} /></span>
              <span className="hp-scan-cta-text">
                <span className="hp-scan-cta-title">{t('home.scanCtaTitle', 'Сканировать QR')}</span>
                <span className="hp-scan-cta-sub">{t('home.scanCtaSub', 'На стенде фудкорта или на чеке')}</span>
              </span>
              <ArrowRight size={20} className="hp-scan-cta-arrow" strokeWidth={2.5} />
            </button>
          </div>

          <motion.div
            className="hp-features"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            <div className="hp-features-label">{t('home.featuresLabel')}</div>

            {/* 1. Заказ без очереди — главная новая фича */}
            <motion.div
              className="hp-feature-card"
              variants={cardVariants}
              whileHover={{ y: -4, boxShadow: '0 8px 32px rgba(212,132,58,0.2)' }}
              whileTap={{ scale: 0.98 }}
            >
              <div className="hp-feature-icon hp-feature-icon--amber"><QrCode size={22} strokeWidth={2} /></div>
              <div>
                <div className="hp-feature-title">{t('home.feature1Title')}</div>
                <div className="hp-feature-desc">{t('home.feature1Desc')}</div>
              </div>
            </motion.div>

            {/* 2. Бронируй стол */}
            <motion.div
              className="hp-feature-card"
              variants={cardVariants}
              whileHover={{ y: -4, boxShadow: '0 8px 32px rgba(212,132,58,0.2)' }}
              whileTap={{ scale: 0.98 }}
            >
              <div className="hp-feature-icon hp-feature-icon--purple"><CalendarCheck size={22} strokeWidth={2} /></div>
              <div>
                <div className="hp-feature-title">{t('home.featureBookingTitle')}</div>
                <div className="hp-feature-desc">{t('home.featureBookingDesc')}</div>
              </div>
            </motion.div>

            {/* 3. Real-time статус */}
            <motion.div
              className="hp-feature-card"
              variants={cardVariants}
              whileHover={{ y: -4, boxShadow: '0 8px 32px rgba(212,132,58,0.2)' }}
              whileTap={{ scale: 0.98 }}
            >
              <div className="hp-feature-icon hp-feature-icon--green"><Bell size={22} strokeWidth={2} /></div>
              <div>
                <div className="hp-feature-title">{t('home.feature2Title')}</div>
                <div className="hp-feature-desc">{t('home.feature2Desc')}</div>
              </div>
            </motion.div>

            {/* 3. Food Passport — dark hero feature */}
            <motion.div
              className="hp-feature-card hp-feature-card--dark"
              variants={cardVariants}
              whileHover={{ y: -4, boxShadow: '0 8px 32px rgba(212,132,58,0.1)' }}
              whileTap={{ scale: 0.98 }}
            >
              <div className="hp-feature-icon hp-feature-icon--passport"><BookOpen size={22} strokeWidth={2} /></div>
              <div>
                <div className="hp-feature-title hp-feature-title--light">{t('home.feature3Title')}</div>
                <div className="hp-feature-desc hp-feature-desc--light">{t('home.feature3Desc')}</div>
              </div>
            </motion.div>

            {/* 4. Докупай пока ждёшь */}
            <motion.div
              className="hp-feature-card"
              variants={cardVariants}
              whileHover={{ y: -4, boxShadow: '0 8px 32px rgba(212,132,58,0.2)' }}
              whileTap={{ scale: 0.98 }}
            >
              <div className="hp-feature-icon hp-feature-icon--blue"><PlusCircle size={22} strokeWidth={2} /></div>
              <div>
                <div className="hp-feature-title">{t('home.feature4Title')}</div>
                <div className="hp-feature-desc">{t('home.feature4Desc')}</div>
              </div>
            </motion.div>

            {/* 5. Друзья и подарки */}
            <motion.div
              className="hp-feature-card"
              variants={cardVariants}
              whileHover={{ y: -4, boxShadow: '0 8px 32px rgba(212,132,58,0.2)' }}
              whileTap={{ scale: 0.98 }}
            >
              <div className="hp-feature-icon hp-feature-icon--pink"><Gift size={22} strokeWidth={2} /></div>
              <div>
                <div className="hp-feature-title">{t('home.feature5Title')}</div>
                <div className="hp-feature-desc">{t('home.feature5Desc')}</div>
              </div>
            </motion.div>

            <div className="hp-cta-wrap">
              <button className="hp-cta-btn" onClick={() => setShowAuth(true)}>
                {t('home.ctaBtn')}
              </button>
              <div className="hp-cta-sub">{t('home.ctaSub')}</div>
            </div>
          </motion.div>

          <div className="hp-footer-links">
            <a
              href="https://business.olso.pro"
              target="_blank"
              rel="noopener noreferrer"
              className="hp-footer-link hp-footer-link--accent"
            >
              {t('home.forRestaurants')} →
            </a>
          </div>

        </div>
      )}

      {/* ── LOGGED IN STATE ── */}
      {isAuthenticated && (
        <div className="hp-loggedin">

          {/* HERO — entry to Food Passport */}
          <div
            className="hp-hero hp-hero--clickable"
            role="button"
            tabIndex={0}
            onClick={() => navigate('/passport')}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); navigate('/passport'); } }}
            aria-label={t('home.passportTitle')}
          >
            <div className="hp-hero-top">
              <div>
                <div className="hp-hero-greeting">{t('home.welcome')}</div>
                <div className="hp-hero-name">{firstName}</div>
              </div>
              <div className="hp-hero-avatar">
                {firstName?.slice(0, 2).toUpperCase()}
              </div>
            </div>
            <div className="hp-hero-stats">
              <div className="hp-hero-stat">
                <div className="hp-hero-stat-val">{customer?.loyaltyPoints || 0}</div>
                <div className="hp-hero-stat-lbl">{t('home.points')}</div>
              </div>
              <div className="hp-hero-stat">
                <div className="hp-hero-stat-val">{customer?.totalOrders || 0}</div>
                <div className="hp-hero-stat-lbl">{t('home.orders')}</div>
              </div>
            </div>
            <div className="hp-hero-cta">
              <BookOpen size={14} strokeWidth={2.2} />
              <span>{t('home.passportTitle')}</span>
              <ChevronRight size={16} className="hp-hero-cta-arrow" />
            </div>
          </div>

          <VenueStrip
            onOpenVenue={(slug) => navigate(`/v/${slug}`)}
          />

          <NearbyStrip
            onOpenFeed={() => navigate('/feed')}
            onOpenRestaurant={(slug) => navigate(`/r/${slug}`)}
          />

          <div className="hp-section-label">{t('home.activeOrder')}</div>

          {(authLoading || loadingOrder) ? (
            <div className="hp-active-order hp-active-order--skel" aria-hidden>
              <div className="hp-active-order-top">
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  <div className="skeleton-block" style={{ width: 92, height: 17, borderRadius: 5 }} />
                  <div className="skeleton-block" style={{ width: 130, height: 12, borderRadius: 4 }} />
                </div>
                <div className="skeleton-block" style={{ width: 86, height: 26, borderRadius: 99 }} />
              </div>
              <div className="hp-progress">
                {[0, 1, 2].map(i => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                    <div className="hp-progress-step">
                      <div className="skeleton-block" style={{ width: 32, height: 32, borderRadius: '50%' }} />
                      <div className="skeleton-block" style={{ width: 40, height: 9, borderRadius: 3, marginTop: 4 }} />
                    </div>
                    {i < 2 && (
                      <div className="skeleton-block" style={{ flex: 1, height: 2, borderRadius: 1, marginBottom: 16 }} />
                    )}
                  </div>
                ))}
              </div>
              <div className="skeleton-block" style={{ width: '100%', height: 42, borderRadius: 12 }} />
            </div>
          ) : activeOrders.length > 0 ? (
            <div className="active-orders-list">
              <AnimatePresence initial={false}>
              {activeOrders.map(activeOrder => activeOrder.status === 'CANCELLED' ? (
                <motion.div
                  key={activeOrder.id}
                  className="hp-active-order hp-active-order--cancelled"
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0, x: 0 }}
                  exit={{ x: '-110%', opacity: 0, transition: { duration: 0.45, ease: [0.4, 0, 0.2, 1] } }}
                  transition={{ duration: 0.4, ease: 'easeOut' }}
                  layout
                >
                  <div className="hp-cancelled-icon" aria-hidden="true">
                    <Frown size={28} strokeWidth={1.8} />
                  </div>
                  <div className="hp-cancelled-title">
                    {t('home.orderCancelledTitle', 'Заказ отменён')}
                  </div>
                  <div className="hp-cancelled-sub">
                    {t('home.orderCancelledSub', 'Деньги вернутся на карту в течение 3-5 рабочих дней')}
                  </div>
                  {activeOrder.totalAmount != null && (
                    <div className="hp-cancelled-amount">
                      {activeOrder.totalAmount.toLocaleString('ru')} ₸
                    </div>
                  )}
                  <button
                    type="button"
                    className="hp-cancelled-dismiss"
                    onClick={() => setActiveOrders(prev => prev.filter(ao => ao.id !== activeOrder.id))}
                  >
                    {t('home.cancelledOk', 'Понятно')}
                  </button>
                </motion.div>
              ) : (
                <motion.div
                  key={activeOrder.id}
                  className="hp-active-order"
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0, x: 0 }}
                  exit={{ x: '-110%', opacity: 0, transition: { duration: 0.45, ease: [0.4, 0, 0.2, 1] } }}
                  transition={{ duration: 0.4, ease: 'easeOut' }}
                  layout
                >
                  <div className="hp-active-order-top">
                    <div>
                      <div className="hp-active-order-id">#{activeOrder.id.slice(-6).toUpperCase()}</div>
                      <div className="hp-active-order-restaurant">{activeOrder.restaurantName}</div>
                    </div>
                    <div className={`hp-status-badge hp-status-badge--${getStatusClass(activeOrder.status)}`}>
                      <div className="hp-status-dot" />
                      {getStatusLabel(activeOrder.status)}
                    </div>
                  </div>

                  <div className={`hp-progress ${activeOrder.status === 'READY' || activeOrder.status === 'COMPLETED' ? 'hp-progress--all-green' : ''}`}>
                    {(['PENDING', 'PREPARING', 'READY'] as const).map((step, i) => {
                      const steps = ['PENDING', 'PREPARING', 'READY'];
                      const currentIdx = steps.indexOf(activeOrder.status);
                      const isActive = i <= currentIdx;
                      const isReadyStep = i === 2 && currentIdx >= 2;
                      const icons = [
                        <ClipboardList size={16} strokeWidth={2} key="p" />,
                        <ChefHat size={16} strokeWidth={2} key="c" />,
                        <CheckCircle size={16} strokeWidth={2} key="r" />,
                      ];
                      const labels = [
                        t('kitchen.status.pending'),
                        t('kitchen.status.preparing'),
                        t('kitchen.status.ready'),
                      ];
                      return (
                        <div key={step} style={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                          <div className="hp-progress-step">
                            <div className={`hp-progress-circle ${isActive ? 'hp-progress-circle--active' : ''} ${isReadyStep ? 'hp-progress-circle--ready' : ''}`}>
                              {icons[i]}
                            </div>
                            <div className="hp-progress-label">{labels[i]}</div>
                          </div>
                          {i < 2 && (
                            <div className={`hp-progress-line ${isActive && i < currentIdx ? 'hp-progress-line--active' : ''}`} />
                          )}
                        </div>
                      );
                    })}
                  </div>

                  <button
                    className="hp-open-order-btn"
                    onClick={() => navigate(`/order/${activeOrder.qrCode}`)}
                  >
                    {t('home.openOrder')} <ArrowRight size={14} style={{ display: 'inline', verticalAlign: 'middle' }} />
                  </button>
                </motion.div>
              ))}
              </AnimatePresence>
            </div>
          ) : (
            <div className="hp-empty-order">
              <div className="hp-empty-order-icon"><QrCode size={40} strokeWidth={1.5} /></div>
              <div className="hp-empty-order-title">{t('home.noActiveOrders')}</div>
              <div className="hp-empty-order-sub">{t('home.noActiveOrdersSub')}</div>
              <button className="hp-empty-order-btn" onClick={handleScanQR}>
                <ScanLine size={16} /> {t('home.scanQR')}
              </button>
            </div>
          )}


          {activeFriends.length > 0 && (
            <>
              <div className="hp-section-label">{t('home.friendsEating', { defaultValue: 'Друзья сейчас едят' })}</div>
              <div className="hp-active-friends">
                {activeFriends.map(af => (
                  <motion.div
                    key={af.friend.id}
                    className="hp-active-friend-card"
                    whileHover={{ y: -4, boxShadow: '0 8px 32px rgba(212,132,58,0.15)' }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => navigate(`/gift?orderId=${af.order.id}&friendId=${af.friend.id}`)}
                  >
                    <div className="hp-af-avatar">
                      {af.friend.name?.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2)}
                    </div>
                    <div className="hp-af-info">
                      <div className="hp-af-name">{af.friend.name}</div>
                      <div className="hp-af-restaurant">{af.order.restaurantName}</div>
                    </div>
                    <div className="hp-af-action"></div>
                  </motion.div>
                ))}
              </div>
            </>
          )}

          <div className="hp-section-label">{t('home.quickActions')}</div>
          <motion.div
            className="hp-quick-actions"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            <motion.div
              className="hp-quick-action"
              variants={cardVariants}
              whileHover={{ y: -4, boxShadow: '0 8px 32px rgba(212,132,58,0.15)' }}
              whileTap={{ scale: 0.98 }}
              onClick={handleScanQR}
            >
              <div className="hp-quick-icon hp-quick-icon--qr"><QrCode size={20} /></div>
              <div className="hp-quick-title">{t('home.scanQRShort')}</div>
              <div className="hp-quick-sub">{t('home.newOrder')}</div>
            </motion.div>
            <motion.div
              className="hp-quick-action"
              variants={cardVariants}
              whileHover={{ y: -4, boxShadow: '0 8px 32px rgba(212,132,58,0.15)' }}
              whileTap={{ scale: 0.98 }}
              onClick={() => navigate('/passport?tab=friends')}
            >
              <div className="hp-quick-icon hp-quick-icon--friends"><Users size={20} /></div>
              <div className="hp-quick-title">{t('home.friends')}</div>
              <div className="hp-quick-sub">{t('home.friendsSub')}</div>
            </motion.div>
            <motion.div
              className="hp-quick-action"
              variants={cardVariants}
              whileHover={{ y: -4, boxShadow: '0 8px 32px rgba(212,132,58,0.15)' }}
              whileTap={{ scale: 0.98 }}
              onClick={() => navigate('/passport?tab=history')}
            >
              <div className="hp-quick-icon hp-quick-icon--history"><History size={20} /></div>
              <div className="hp-quick-title">{t('home.history')}</div>
              <div className="hp-quick-sub">{customer?.totalOrders || 0} {t('home.pastOrders')}</div>
            </motion.div>
          </motion.div>

        </div>
      )}

      {showAuth && (
        <FirebaseAuthModal
          onClose={() => setShowAuth(false)}
          onSuccess={() => setShowAuth(false)}
        />
      )}

      {showScanner && (
        <QrScannerModal
          onClose={() => setShowScanner(false)}
          onScan={handleScanResult}
        />
      )}

      {pendingRefCode && (
        <FriendInviteModal
          referralCode={pendingRefCode}
          token={token}
          onAccepted={() => {
            setPendingRefCode(null);
            setRefToast({ msg: t('friends.added', 'Вы стали друзьями! +50 баллов 🎉'), type: 'success' });
            setTimeout(() => setRefToast(null), 4000);
          }}
          onDeclined={() => setPendingRefCode(null)}
          onSignInRequired={() => setShowAuth(true)}
        />
      )}

      <LegalFooter />

      {/* Debug panel */}
      {!DEBUG_PANEL ? null : createPortal(
        <div style={{
          position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 99999,
          background: 'var(--a-debug-bg)', color: '#0f0', fontSize: 10, fontFamily: 'monospace',
          borderTop: '2px solid #333', maxHeight: debugOpen ? '40vh' : 28,
          overflow: 'hidden', transition: 'max-height 0.2s',
        }}>
          <div
            onClick={() => setDebugOpen(o => !o)}
            style={{
              padding: '4px 10px', background: 'var(--a-debug-header)', cursor: 'pointer',
              display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #333',
            }}
          >
            <span> DEBUG {debugLines.length} lines</span>
            <span>{debugOpen ? '▼' : '▲'}</span>
          </div>
          {debugOpen && (
            <div style={{ padding: '6px 10px', overflowY: 'auto', maxHeight: 'calc(40vh - 28px)', whiteSpace: 'pre-wrap', wordBreak: 'break-all', lineHeight: 1.5 }}>
              {debugLines.map((line, i) => (
                <div key={i} style={{ color: line.includes('FAIL') || line.includes('error') ? '#f55' : line.includes('') || line.includes('OK') ? '#5f5' : '#0f0' }}>
                  {line}
                </div>
              ))}
              {debugLines.length === 0 && <div style={{ color: '#666' }}>waiting for logs...</div>}
            </div>
          )}
        </div>,
        document.body
      )}
    </div>
  );
}
